import { ArrowLeft, Download } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { Button } from '@/shared/components/ui/button'
import { toast } from '@/shared/lib/toast'

import { downloadPurchaseOrderPDF } from '../api/purchaseOrder.api'

export default function PurchaseOrderPdfPreviewPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const purchaseOrderId = searchParams.get('id')
  const poNumber = searchParams.get('poNumber')

  const [pdfUrl, setPdfUrl] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let objectUrl = ''

    const loadPdf = async () => {
      if (!purchaseOrderId) {
        toast.error('Purchase Order ID is missing')
        setLoading(false)
        return
      }

      try {
        const blob = await downloadPurchaseOrderPDF(purchaseOrderId)

        objectUrl = URL.createObjectURL(blob)
        setPdfUrl(objectUrl)
      } catch (error) {
        toast.error(error?.message || 'Failed to load purchase order PDF')
      } finally {
        setLoading(false)
      }
    }

    loadPdf()

    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl)
      }
    }
  }, [purchaseOrderId])

  const handleDownload = () => {
    if (!pdfUrl) return

    const link = document.createElement('a')
    link.href = pdfUrl
    link.download = `purchase-order-${poNumber || purchaseOrderId || 'export'}.pdf`

    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <div className="flex h-screen flex-col bg-slate-100">
      {/* Header */}
      <div className="flex shrink-0 items-center justify-between border-b bg-white px-6 py-4">
        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/purchase?tab=orders')}
            className="rounded-xl"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>

          <h1 className="text-xl font-bold text-slate-900">
            Purchase Order PDF Preview
          </h1>
        </div>

        <Button
          type="button"
          onClick={handleDownload}
          disabled={!pdfUrl || loading}
          className="rounded-xl bg-[#111827] px-5 text-white hover:bg-[#1f2937]"
        >
          <Download className="mr-2 h-4 w-4" />
          Download
        </Button>
      </div>

      {/* PDF Preview */}
      <div className="min-h-0 flex-1 p-4">
        {loading ? (
          <div className="flex h-full items-center justify-center rounded-xl bg-white">
            <p className="text-sm text-slate-500">
              Loading purchase order preview...
            </p>
          </div>
        ) : pdfUrl ? (
          <iframe
            src={pdfUrl}
            title="Purchase Order PDF Preview"
            className="h-full w-full rounded-xl border border-slate-200 bg-white"
          />
        ) : (
          <div className="flex h-full items-center justify-center rounded-xl bg-white">
            <p className="text-sm text-red-500">
              Unable to load purchase order PDF.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
